import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Mail, Lock, User, Phone } from "lucide-react";

// Logo URLs
const EUROCALL_LOGO = "https://customer-assets.emergentagent.com/job_b17cad2c-20e2-43f4-94ab-e2a3873cd763/artifacts/ua87jms9_logo%20eurocall2.png";
const AQS_LOGO = "https://customer-assets.emergentagent.com/job_contract-hub-53/artifacts/0e2l56xd_334555.webp";

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ 
    email: "", 
    password: "", 
    name: "", 
    role: "agent" 
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Autentificare reușită!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la autentificare");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.name, registerData.role);
      toast.success("Cont creat cu succes!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la înregistrare");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1e3a8a] via-[#1e40af] to-[#3b82f6] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          {/* Eurocall Logo */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl mb-8">
            <img 
              src={EUROCALL_LOGO} 
              alt="EuroCall" 
              className="h-40 w-auto object-contain"
            />
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4 text-center">
            EuroCall CRM
          </h1>
          <p className="text-xl text-blue-100 text-center max-w-md">
            Sistem integrat pentru managementul contractelor de furnizare energie
          </p>
          
          {/* Features */}
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3 text-blue-100">
              <div className="w-8 h-8 rounded-full bg-blue-500/30 flex items-center justify-center">
                <Phone className="h-4 w-4 text-white" />
              </div>
              <span>Gestionare clienți și contracte</span>
            </div>
            <div className="flex items-center gap-3 text-blue-100">
              <div className="w-8 h-8 rounded-full bg-blue-500/30 flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
              <span>Monitorizare agenți și vânzări</span>
            </div>
            <div className="flex items-center gap-3 text-blue-100">
              <div className="w-8 h-8 rounded-full bg-blue-500/30 flex items-center justify-center">
                <Mail className="h-4 w-4 text-white" />
              </div>
              <span>Raportare și statistici în timp real</span>
            </div>
          </div>
          
          {/* AQS Logo at bottom */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-3 text-blue-200 text-sm">
              <span>Powered by</span>
              <div className="bg-white rounded-lg px-3 py-1.5">
                <img 
                  src={AQS_LOGO} 
                  alt="AQS - Advance Quality Service" 
                  className="h-6 w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img 
              src={EUROCALL_LOGO} 
              alt="EuroCall" 
              className="h-28 w-auto object-contain mb-4"
            />
            <h1 className="text-2xl font-bold text-[#1e3a8a]">EuroCall CRM</h1>
          </div>

          {/* Desktop Logo */}
          <div className="hidden lg:flex items-center gap-3 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1e3a8a]">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1e3a8a] tracking-tight">
                EuroCall
              </h1>
              <p className="text-sm text-slate-500">Contract Management System</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <Tabs defaultValue="login" className="w-full">
              <CardHeader className="pb-4">
                <TabsList className="grid w-full grid-cols-2 bg-slate-100">
                  <TabsTrigger 
                    value="login" 
                    data-testid="login-tab"
                    className="data-[state=active]:bg-[#1e3a8a] data-[state=active]:text-white"
                  >
                    Autentificare
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    data-testid="register-tab"
                    className="data-[state=active]:bg-[#1e3a8a] data-[state=active]:text-white"
                  >
                    Înregistrare
                  </TabsTrigger>
                </TabsList>
              </CardHeader>
              
              <CardContent>
                <TabsContent value="login" className="mt-0">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="email@exemplu.ro"
                          className="pl-10 focus:ring-[#1e3a8a] focus:border-[#1e3a8a]"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          data-testid="login-email"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Parolă</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10 focus:ring-[#1e3a8a] focus:border-[#1e3a8a]"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password"
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white"
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? "Se procesează..." : "Autentificare"}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="register" className="mt-0">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nume complet</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Ion Popescu"
                          className="pl-10"
                          value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                          required
                          data-testid="register-name"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="email@exemplu.ro"
                          className="pl-10"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          required
                          data-testid="register-email"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Parolă</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          required
                          data-testid="register-password"
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white"
                      disabled={loading}
                      data-testid="register-submit"
                    >
                      {loading ? "Se procesează..." : "Creare cont"}
                    </Button>
                  </form>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
          
          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              © 2025 EuroCall - Powered by AQS
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
